import {
  Button,
  Input,
  Search,
  Users,
  formatPhoneNumberForDisplay,
} from "@ews-admin/global-design-system";
import { Select } from "../../components/atoms/Select";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { ConfirmationModal } from "../../components/molecules/ConfirmationModal/ConfirmationModal";
import { Pagination } from "../../components/molecules/Pagination";
import { adminUsersApi } from "../../services/adminUsers.api";
import {
  AdminUser,
  AdminUserPage,
  AdminUserRole,
  AdminUserStatus,
} from "../../types/adminUser.definition";

export const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const ROLES: AdminUserRole[] = [
  "PATIENT",
  "DOCTOR",
  "MANAGER",
  "SECRETARY",
  "ADMIN",
  "SUPER_ADMIN",
];
const STATUSES: AdminUserStatus[] = ["ACTIVE", "UNVERIFIED", "DISABLED"];

/** Admin accounts are not managed from this directory: it never offers to disable them. */
const PROTECTED_ROLES: (AdminUserRole | null)[] = ["ADMIN", "SUPER_ADMIN"];

const STATUS_BADGE: Record<AdminUserStatus, string> = {
  ACTIVE: "bg-success-100 text-success-800",
  UNVERIFIED: "bg-warning-100 text-warning-800",
  DISABLED: "bg-danger-100 text-danger-800",
};

type UserAction = "disable" | "enable" | "verify";

const CONFIRM_VARIANT: Record<UserAction, "danger" | "success" | "info"> = {
  disable: "danger",
  enable: "success",
  verify: "info",
};

function oneOf<T extends string>(values: T[], value: string | null) {
  return values.find(item => item === value);
}

/**
 * MED-1341: every account of the platform, with search, filters and pagination. The filters live in
 * the URL (q, role, status, from, to, page) so any page — Aperçu first — can link to a filtered list.
 */
export const UsersManagement: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const keyword = searchParams.get("q") ?? "";
  const role = oneOf(ROLES, searchParams.get("role"));
  const status = oneOf(STATUSES, searchParams.get("status"));
  const createdFrom = searchParams.get("from") ?? "";
  const createdTo = searchParams.get("to") ?? "";
  const page = Math.max(1, Number(searchParams.get("page")) || 1);
  const hasFilters = Boolean(
    keyword || role || status || createdFrom || createdTo
  );

  const [data, setData] = useState<AdminUserPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [pending, setPending] = useState<{
    action: UserAction;
    user: AdminUser;
  } | null>(null);
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState(false);

  /** Any filter change goes back to page 1; only the pagination keeps the page. */
  const updateParams = useCallback(
    (changes: Record<string, string>, keepPage = false) => {
      setSearchParams(previous => {
        const next = new URLSearchParams(previous);
        Object.entries(changes).forEach(([key, value]) =>
          value ? next.set(key, value) : next.delete(key)
        );
        if (!keepPage) {
          next.delete("page");
        }
        return next;
      });
    },
    [setSearchParams]
  );

  // The search box follows the URL (reset, back button) unless it only differs by spaces being typed.
  const [searchTerm, setSearchTerm] = useState(keyword);
  const [syncedKeyword, setSyncedKeyword] = useState(keyword);
  if (keyword !== syncedKeyword) {
    setSyncedKeyword(keyword);
    if (keyword !== searchTerm.trim()) {
      setSearchTerm(keyword);
    }
  }

  useEffect(() => {
    const trimmed = searchTerm.trim();
    if (trimmed === keyword) {
      return;
    }
    const timer = setTimeout(
      () => updateParams({ q: trimmed }),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [searchTerm, keyword, updateParams]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    adminUsersApi
      .search(
        {
          keyword: keyword || undefined,
          role,
          status,
          createdFrom: createdFrom || undefined,
          createdTo: createdTo || undefined,
        },
        page - 1,
        PAGE_SIZE
      )
      .then(result => {
        if (!cancelled) {
          setData(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setData(null);
          setLoadError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [keyword, role, status, createdFrom, createdTo, page, reloadKey]);

  const resetFilters = () => {
    setSearchTerm("");
    setSearchParams(new URLSearchParams());
  };

  const confirmAction = async () => {
    if (!pending) return;
    setActing(true);
    setActionError(false);
    try {
      await adminUsersApi[pending.action](pending.user.id);
      setReloadKey(key => key + 1);
    } catch {
      setActionError(true);
    } finally {
      setActing(false);
      setPending(null);
    }
  };

  const displayName = (user: AdminUser) =>
    `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() ||
    user.email ||
    user.phoneNumber ||
    `#${user.id}`;

  const signUpDate = (user: AdminUser) =>
    user.createdAt
      ? new Date(user.createdAt).toLocaleDateString(i18n.language, {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "—";

  const users = data?.content ?? [];

  return (
    <div className="page-container">
      <div className="flex flex-wrap gap-4 justify-between items-center page-header">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t("admin.users.title")}
          </h2>
          <p className="text-gray-600">{t("admin.users.subtitle")}</p>
        </div>
        {data && (
          <span className="text-sm font-medium text-gray-700">
            {t("admin.users.total", { count: data.totalElements })}
          </span>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 gap-4 items-end page-section md:grid-cols-3 xl:grid-cols-6">
        <div className="md:col-span-3 xl:col-span-2">
          <Input
            placeholder={t("admin.users.searchPlaceholder")}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            leftIcon={<Search className="w-4 h-4" />}
            aria-label={t("admin.users.searchPlaceholder")}
          />
        </div>
        <Select
          label={t("admin.users.filters.role")}
          value={role ?? ""}
          onChange={value => updateParams({ role: String(value) })}
          options={[
            { label: t("admin.users.filters.allRoles"), value: "" },
            ...ROLES.map(item => ({
              label: t(`admin.users.roles.${item}`),
              value: item,
            })),
          ]}
        />
        <Select
          label={t("admin.users.filters.status")}
          value={status ?? ""}
          onChange={value => updateParams({ status: String(value) })}
          options={[
            { label: t("admin.users.filters.allStatuses"), value: "" },
            ...STATUSES.map(item => ({
              label: t(`admin.users.statuses.${item}`),
              value: item,
            })),
          ]}
        />
        <Input
          type="date"
          label={t("admin.users.filters.createdFrom")}
          value={createdFrom}
          max={createdTo || undefined}
          onChange={e => updateParams({ from: e.target.value })}
        />
        <Input
          type="date"
          label={t("admin.users.filters.createdTo")}
          value={createdTo}
          min={createdFrom || undefined}
          onChange={e => updateParams({ to: e.target.value })}
        />
      </div>
      {hasFilters && (
        <div className="flex justify-end -mt-2 page-section">
          <Button variant="outline" size="sm" onClick={resetFilters}>
            {t("admin.users.filters.reset")}
          </Button>
        </div>
      )}

      {(loadError || actionError) && (
        <div
          role="alert"
          className="p-3 text-red-700 bg-red-100 rounded border border-red-400 page-section"
        >
          {t(loadError ? "admin.users.loadError" : "admin.users.actionError")}
        </div>
      )}

      <div className="page-section">
        {loading && !data ? (
          <div className="py-12 text-center">
            <p className="text-sm text-neutral-500">{t("common.loading")}</p>
          </div>
        ) : !loadError && users.length === 0 ? (
          <div className="py-12 text-center">
            <Users className="mx-auto w-12 h-12 text-neutral-400" />
            <h3 className="mt-2 text-sm font-medium text-neutral-900">
              {t("admin.users.noUsers")}
            </h3>
            <p className="mt-1 text-sm text-neutral-500">
              {t("admin.users.noUsersDescription")}
            </p>
          </div>
        ) : (
          users.length > 0 && (
            <>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="min-w-full text-sm divide-y divide-gray-200">
                  <thead className="text-xs font-medium tracking-wider text-gray-500 uppercase bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        {t("admin.users.table.name")}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t("admin.users.table.role")}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t("admin.users.table.email")}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t("admin.users.table.phone")}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t("admin.users.table.signedUp")}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t("admin.users.table.status")}
                      </th>
                      <th className="px-4 py-3 text-right">
                        {t("common.actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {users.map(user => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                          {displayName(user)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full bg-neutral-100 text-neutral-800">
                            {t(`admin.users.roles.${user.role ?? "NONE"}`)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {user.email ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {user.phoneNumber
                            ? formatPhoneNumberForDisplay(user.phoneNumber)
                            : "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {signUpDate(user)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${STATUS_BADGE[user.status]}`}
                          >
                            {t(`admin.users.statuses.${user.status}`)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <div className="flex gap-2 justify-end">
                            {user.status === "UNVERIFIED" && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setPending({ action: "verify", user })
                                }
                              >
                                {t("admin.users.actions.verify")}
                              </Button>
                            )}
                            {user.status === "DISABLED" ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setPending({ action: "enable", user })
                                }
                              >
                                {t("admin.users.actions.enable")}
                              </Button>
                            ) : (
                              !PROTECTED_ROLES.includes(user.role) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    setPending({ action: "disable", user })
                                  }
                                >
                                  {t("admin.users.actions.disable")}
                                </Button>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {data && data.totalPages > 1 && (
                <div className="page-section">
                  <Pagination
                    currentPage={page}
                    totalPages={data.totalPages}
                    totalItems={data.totalElements}
                    itemsPerPage={PAGE_SIZE}
                    onPageChange={next =>
                      updateParams({ page: next > 1 ? String(next) : "" }, true)
                    }
                    showPageNumbers={true}
                    maxVisiblePages={5}
                    showItemsPerPageSelector={false}
                  />
                </div>
              )}
            </>
          )
        )}
      </div>

      <ConfirmationModal
        isOpen={pending !== null}
        onClose={() => setPending(null)}
        onConfirm={confirmAction}
        title={pending ? t(`admin.users.confirm.${pending.action}Title`) : ""}
        message={
          pending
            ? t(`admin.users.confirm.${pending.action}Message`, {
                name: displayName(pending.user),
              })
            : ""
        }
        confirmLabel={
          pending ? t(`admin.users.actions.${pending.action}`) : undefined
        }
        cancelLabel={t("common.cancel")}
        variant={pending ? CONFIRM_VARIANT[pending.action] : "info"}
        isLoading={acting}
      />
    </div>
  );
};
